import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import {
    Component,
    inject,
    OnInit,
    signal,
    ViewEncapsulation,
} from '@angular/core';
import {
    FormBuilder,
    FormGroup,
    FormsModule,
    ReactiveFormsModule,
    Validators,
} from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { fuseAnimations } from '@fuse/animations';
import { FuseAlertComponent, FuseAlertType } from '@fuse/components/alert';
import { CopyrightComponent } from 'app/components/copyright/copyright.component';
import { StartupLogoComponent } from 'app/components/startup-logo/startup-logo.component';
import { VerificationCodeComponent } from 'app/components/verification-code/verification-code.component';
import { AuthService } from 'app/core/auth/auth.service';
import { Firebase2FAService } from 'app/core/auth/firebase/firebase-2fa.service';
import { FirebaseService } from 'app/core/auth/firebase/firebase.service';
import { AlertService } from 'app/services/alert.service';
import { LoggerService } from 'app/services/logger.service';
import { environment } from 'environments/environment';
import { initializeApp } from 'firebase/app';
import {
    getAuth,
    MultiFactorError,
    MultiFactorResolver,
    RecaptchaVerifier,
} from 'firebase/auth';
import { catchError, EMPTY, from, Observable, retry } from 'rxjs';
import { redirectErrors } from './two-fa.config';

const MatModules = [
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatIconModule,
    MatCheckboxModule,
    MatProgressSpinnerModule,
];

const enum userStateTypes {
    ENROLL_FIRST = 'enroll-first',
    ALREADY_ENROLLED = 'already-enrolled',
}

@Component({
    selector: 'app-two-fa',
    encapsulation: ViewEncapsulation.None,
    animations: fuseAnimations,
    standalone: true,
    imports: [
        ...MatModules,
        CommonModule,
        RouterLink,
        FuseAlertComponent,
        FormsModule,
        ReactiveFormsModule,
        StartupLogoComponent,
        CopyrightComponent,
        VerificationCodeComponent,
    ],
    templateUrl: './two-fa.component.html',
})
export class TwoFaComponent implements OnInit {
    private readonly _fb = inject(FormBuilder);
    private app = initializeApp(environment.firebaseConf);
    private auth = getAuth(this.app);

    twoFaForm: FormGroup;
    user: any | null = null;

    verificationId: string | null = null;
    resolver: MultiFactorResolver | null = null;
    recaptchaVerifier!: RecaptchaVerifier;
    isRecaptchaSolved = signal<boolean>(false);
    enableCodeInput = signal<boolean>(false);
    resetCodeInput = signal<boolean>(false);

    private _buildForm(): void {
        this.twoFaForm = this._fb.group({
            phoneNumber: ['', Validators.required],
            code: [
                '',
                [
                    Validators.required,
                    Validators.minLength(6),
                    Validators.maxLength(6),
                ],
            ],
            rememberMe: [''],
        });
    }

    alert: { type: FuseAlertType; message: string } = {
        type: 'success',
        message: '',
    };
    showAlert: boolean = false;

    constructor(
        private _alertService: AlertService,
        private _firebaseService: FirebaseService,
        private _activatedRoute: ActivatedRoute,
        private _firebase2FAService: Firebase2FAService,
        private _authService: AuthService,
        private logger: LoggerService,
        private _router: Router
    ) {}

    // ----------------------------------------------------------------------
    // @ Lifecycle hooks
    // ----------------------------------------------------------------------

    ngOnInit(): void {
        this._buildForm();
    }

    ngAfterViewInit(): void {
        // Wait for the catha to be ready
        this.initRecaptchaVerifier()
            .pipe(
                retry(1),
                catchError((err) => {
                    this.alert = {
                        type: 'error',
                        message: 'ReCAPTCHA failed. Please try again.',
                    };
                    this.showAlert = true;
                    return EMPTY;
                })
            )
            .subscribe((widgetId) => {
                // console.log('ReCAPTCHA rendered with id:', widgetId);

                // Determine the state return
                const status = this.handleStateReturn();

                // 1  Enroll MFA
                if (status === userStateTypes.ENROLL_FIRST) {
                    console.log('Proceeding to MFA enrollment');
                    this.enrollMfa();
                    return;
                }

                // 2  Already enrolled, proceed to 2FA verification
                if (status === userStateTypes.ALREADY_ENROLLED) {
                    console.log('Proceeding to 2FA verification');
                    this.setRecaptchaVerifierFor2fa();
                }
            });
    }

    // ----------------------------------------------------------------------
    // @ Private methods
    // ----------------------------------------------------------------------

    private initRecaptchaVerifier(): Observable<any> {
        this.recaptchaVerifier?.clear();
        this.recaptchaVerifier = new RecaptchaVerifier(
            this.auth,
            'recaptcha-container',
            {
                size: 'invisible',
                callback: (token: string) => {
                    this.isRecaptchaSolved.set(true);
                },
                'expired-callback': () => {
                    console.warn('Recaptcha expired, please try again');
                },
            }
        );

        return from(this.recaptchaVerifier.render());
    }

    private handleStateReturn(): userStateTypes {
        const state = history.state;
        this.user = this.auth.currentUser;
        // this.logger.log('Current User', this.user);

        if (this.user === null && !state.firebaseError) {
            this._alertService.showAlert(
                'No authenticated user found for 2FA process',
                'info'
            );
            this._router.navigateByUrl('/sign-in');
        }

        const userState = !state.firebaseError
            ? userStateTypes.ENROLL_FIRST
            : userStateTypes.ALREADY_ENROLLED;

        return userState;
    }

    // ----------------------------------------------------------------------
    // @ Public methods
    // ----------------------------------------------------------------------

    /**
     * Verify
     */
    onFillCodeInput(code: string): void {
        // Disable the form
        // this.twoFaForm.disable();

        // Hide the alert
        this.showAlert = false;

        // Determine the state return
        if (this.handleStateReturn() === userStateTypes.ENROLL_FIRST) {
            this.enrollVerifyCode(code);
        } else if (
            this.handleStateReturn() === userStateTypes.ALREADY_ENROLLED
        ) {
            this.authVerifyCode(code);
            return;
        }
    }

    // finalizeLogin(idToken2fa: string): void {
    //     this._authService.complete2FaSignIn(idToken2fa);
    //     // this._router.navigateByUrl('/dashboard');
    // }

    finalizeLogin(idToken2fa: string): void {
        this._authService.complete2FaSignIn(idToken2fa).subscribe({
            next: (res) => {
                // console.log(res);
                this._router.navigateByUrl('/dashboard');
            },
            error: (err: HttpErrorResponse) => {
                console.error('2FA completion error:', err);
                const message =
                    err.status === 401
                        ? 'Invalid authentication token. Please try signing in again.'
                        : 'An error occurred during authentication. Please try again.';
                this._alertService.showAlert(message, 'error');

                // Re-enable the code input
                this.reenableInput();
            },
        });
    }

    reenableInput(): void {
        this.enableCodeInput.set(true);
        this.resetCodeInput.set(true);
        setTimeout(() => {
            this.resetCodeInput.set(false);
        }, 0);
    }

    // ----------------------------------------------------------------------
    // @ Enrollment methods
    // ----------------------------------------------------------------------

    enrollMfa(): void {
        this._firebase2FAService
            .enrollMfaSendSms(
                this.auth.currentUser,
                this.user.phoneNumber,
                this.recaptchaVerifier
            )
            .subscribe({
                next: (verificationId) => {
                    console.log('Enrollment SMS sent');
                    this.verificationId = verificationId;

                    // Re-enable the code input
                    this.reenableInput();
                },
                error: (error: MultiFactorError) => {
                    if (redirectErrors.has(error.code)) {
                        console.log('Redirecting due to error:', error.code);
                        this._alertService.showAlert(
                            'Your session has expired or is invalid. Please sign in again.',
                            'info'
                        );
                        this._router.navigateByUrl('/sign-in');
                        return;
                    }

                    this.alert = {
                        type: 'error',
                        message:
                            this._firebaseService.getFirebaseErrorMessage(
                                error
                            ),
                    };
                    this.showAlert = true;

                    // Re-enable the code input
                    this.reenableInput();
                },
            });
    }

    // ----------------------------------------------------------------------
    // @ 2FA methods
    // ----------------------------------------------------------------------

    setRecaptchaVerifierFor2fa(): void {
        this._firebase2FAService.startError$.subscribe((error) => {
            if (redirectErrors.has(error?.code)) {
                this._alertService.showAlert(
                    'Your session has expired or is invalid. Please sign in again.',
                    'info'
                );
                this._router.navigateByUrl('/sign-in');
                return;
            }

            this.alert = {
                type: 'error',
                message: this._firebaseService.getFirebaseErrorMessage(error),
            };
            this.showAlert = true;
        });

        this._firebase2FAService.recaptchaVerifier = this.recaptchaVerifier;

        // Enable the code input
        this.enableCodeInput.set(true);
    }

    // ----------------------------------------------------------------------
    // @ Verify Code
    // ----------------------------------------------------------------------

    /**
     * @description
     * Enrollment verification of the code sent via SMS
     */
    enrollVerifyCode(code: string): void {
        this.enableCodeInput.set(false);

        this._firebase2FAService
            .confirmEnrollMfa(this.auth.currentUser, this.verificationId, code)
            .subscribe({
                next: () => {
                    this.logger.log('MFA enrollment completed successfully');

                    this.alert = {
                        type: 'success',
                        message:
                            'Your phone number has been successfully enrolled for 2FA. You can now use it for future sign-ins. Redirecting to sign-in...',
                    };

                    this.showAlert = true;

                    setTimeout(() => {
                        this._router.navigateByUrl('/sign-in');
                    }, 5000);
                },
                error: (error) => {
                    console.log('Enrollment verification failed:', error);
                    this.alert = {
                        type: 'error',
                        message:
                            this._firebaseService.getFirebaseErrorMessage(
                                error
                            ),
                    };
                    this.showAlert = true;

                    // Re-enable the code input
                    this.reenableInput();
                },
            });
    }

    authVerifyCode(code: string): void {
        this.enableCodeInput.set(false);

        this._firebase2FAService.complete2fa(code).subscribe({
            next: async (userCredential) => {
                // console.log('UserCredential:', userCredential);
                const user = userCredential.user;
                const idToken = await user.getIdToken(true);
                this.finalizeLogin(idToken);
            },
            error: (error) => {
                console.log('2FA verification failed:', error.message);
                if (error.message === 'No 2FA challenge found') {
                    this._alertService.showAlert(
                        'No authenticated user found for 2FA process',
                        'info'
                    );

                    // this._router.navigateByUrl('/sign-in');
                    return;
                }

                this.alert = {
                    type: 'error',
                    message:
                        this._firebaseService.getFirebaseErrorMessage(error),
                };

                this.showAlert = true;

                // Re-enable the code input
                this.reenableInput();
            },
        });
    }
}
